import React, { useState } from "react";
import { Box, Typography, IconButton, CircularProgress, makeStyles } from "@material-ui/core";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { VehiclePanel } from "./VehiclePanel";
import { MapModule } from "./MapModule";
import { VehicleDetailPanel } from "./VehicleDetailPanel";
import { TrackingHistoryBar } from "./TrackingHistoryBar";
import { useVehicleTracking } from "./useVehicleTracking";
import { EVENT_CONFIG, statusColor } from "../constants";
import { Vehicle } from "../types";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", height: "100%", position: "relative" },
  panelWrap: { flexShrink: 0, height: "100%", zIndex: 10, boxShadow: theme.shadows[4], position: "relative" },
  loadingOverlay: { position: "absolute", inset: 0, backgroundColor: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center" },
  expandBtn: {
    position: "absolute",
    left: 8,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 20,
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    width: 24,
    height: 24,
    boxShadow: theme.shadows[2],
    "&:hover": { backgroundColor: "#f9fafb" },
  },
  mapWrap: { flex: 1, position: "relative", overflow: "hidden" },
  historyBarWrap: { paddingRight: 340, transition: "padding 0.2s" },
  statusToast: {
    position: "absolute",
    bottom: 80,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    backgroundColor: "color-mix(in srgb, var(--brand-accent) 90%, transparent)",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    fontSize: 12,
  },
  legendBox: {
    position: "absolute",
    zIndex: 1000,
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: 12,
    boxShadow: theme.shadows[4],
    border: "1px solid #f3f4f6",
    padding: "10px 12px",
  },
  eventsLegend: { bottom: 64, left: 16 },
  statusLegend: { bottom: 20, left: 20, display: "flex", flexDirection: "column", gap: 6 },
  legendTitle: { fontSize: 10, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  legendEmpty: { fontSize: 11, color: "#9ca3af" },
  legendRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#4b5563", marginBottom: 4 },
  legendDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  capitalize: { textTransform: "capitalize" },
}));

interface LiveMapProps {
  vehicles: Vehicle[];
  loading: boolean;
  totalCount: number;
  onlineCount: number;
}

export function LiveMap({ vehicles, loading, totalCount, onlineCount }: LiveMapProps) {
  const classes = useStyles();
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [trackingHours, setTrackingHours] = useState<number | null>(null);

  const selectedVehicle = vehicles.find((v) => v.id === selectedId);
  const { trackingWindow, stats: periodStats, loading: trackingLoading, unavailable: trackingUnavailable } = useVehicleTracking(selectedVehicle, trackingHours);

  const handleSelect = (id: string) => {
    setSelectedId(id || null);
    if (!id) setTrackingHours(null);
  };

  const handleTrackingHours = (h: number) => setTrackingHours(h === trackingHours ? null : h);

  return (
    <Box className={classes.root}>
      {panelOpen && (
        <Box className={classes.panelWrap}>
          <VehiclePanel
            vehicles={vehicles}
            selectedId={selectedId}
            onSelect={handleSelect}
            onClose={() => setPanelOpen(false)}
            totalCount={totalCount}
            onlineCount={onlineCount}
          />
          {loading && (
            <Box className={classes.loadingOverlay}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      )}

      {!panelOpen && (
        <IconButton onClick={() => setPanelOpen(true)} className={classes.expandBtn}>
          <ChevronRightIcon style={{ fontSize: 16, color: "#6b7280" }} />
        </IconButton>
      )}

      <Box className={classes.mapWrap}>
        <MapModule vehicles={vehicles} selectedId={selectedId} onSelect={handleSelect} trackingWindow={trackingWindow} />

        {selectedVehicle && (
          <VehicleDetailPanel
            vehicle={selectedVehicle}
            onClose={() => { setSelectedId(null); setTrackingHours(null); }}
            trackingHours={trackingHours}
            periodStats={periodStats}
          />
        )}

        {selectedVehicle && (
          <Box className={classes.historyBarWrap}>
            <TrackingHistoryBar selected={trackingHours} onChange={handleTrackingHours} />
          </Box>
        )}

        {trackingHours && trackingLoading && (
          <Box className={classes.statusToast}>Loading tracking history…</Box>
        )}

        {trackingHours && !trackingLoading && trackingUnavailable && (
          <Box className={classes.statusToast}>No tracking history available for this vehicle/window.</Box>
        )}

        {trackingWindow ? (
          <Box className={`${classes.legendBox} ${classes.eventsLegend}`}>
            <Typography className={classes.legendTitle}>Events</Typography>
            {trackingWindow.events.length === 0 ? (
              <Typography className={classes.legendEmpty}>No flagged events for this vehicle's device.</Typography>
            ) : (
              Object.entries(EVENT_CONFIG).map(([key, cfg]) => (
                <Box key={key} className={classes.legendRow}>
                  <Box className={classes.legendDot} style={{ backgroundColor: cfg.color }} />
                  {cfg.label}
                </Box>
              ))
            )}
          </Box>
        ) : (
          <Box className={`${classes.legendBox} ${classes.statusLegend}`}>
            {Object.entries(statusColor).map(([s, c]) => (
              <Box key={s} className={classes.legendRow} style={{ marginBottom: 0 }}>
                <Box className={classes.legendDot} style={{ backgroundColor: c }} />
                <Box component="span" className={classes.capitalize}>{s}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
